// combatpro/encounter/encounterEngine.js
//
// Combat PRO - Encounter Engine (Stage 46: pure encounter summary logic;
// Stage 65: balance bar, threat assessment, and system-state aggregation).
//
// Pure, UI-independent encounter logic extracted from the encounter runtime in
// battletracker.html (the read-only master spec). No React, no DOM, no context,
// no state, no side effects, no cloud / save. It receives the monsters array and
// the active combatant count and returns plain aggregate data; a small display
// helper turns the aggregates into the exact summary strings the legacy rendered.
//
// Source mapping (battletracker.html):
//   - computeEncounterSummary(monsters, activeMonsterCount)
//       the aggregation half of updateEncounterSummary (9391-9410): iterate the
//       active monsters, SKIP unconfigured ones (maxHP === 0 AND no
//       selectedMonster), and accumulate totalCurrentHP, totalMaxHP, activeCount
//       (currentHP > 0), and legendaryActionsRemaining (sum of positive
//       maxLegendaryActions - usedLegendaryActions). Reading the legendary fields
//       is a read of inert monster data; the Legendary Runtime is NOT migrated.
//   - encounterSummaryDisplay(summary)
//       the display formatting (9413-9415): when totalMaxHP > 0, totalHP is
//       "current/max" and the counts are shown; otherwise every field is "-".
//
// Stage 65 adds three further pure helpers, ported verbatim in behaviour from the
// remaining encounter-page logic the Players Runtime (Stage 48C-0) now makes
// migratable (the legacy read window.viPlayers; here players are passed in from
// state.players):
//   - computeBalanceBar(players, monsters, activeMonsterCount)
//       viUpdateBalanceBar (8954-8969): playersHP = sum of player hp; monstersHP =
//       sum of currentHP over the first activeMonsterCount monsters (NO skip);
//       total = playersHP + monstersHP || 1; each side's percent is
//       (hp / total * 100).toFixed(1) (a string, e.g. "0.0"). Returns the two HP
//       numbers and the two percent strings the legacy wrote into #vi-bal-players /
//       #vi-bal-monsters (text) and #vi-bal-players-bar / #vi-bal-monsters-bar
//       (width = percent + "%").
//   - computeThreat(players, monsters, activeMonsterCount)
//       updateThreatAssessment (9439-9508). partySize / partyHP are derived from
//       the players exactly as updateEncounterSummary fed the hidden inputs before
//       calling it (9421-9422): partySize = players.length || 4, partyHP = sum of
//       player maxHp || 120. partyLevel is the legacy constant 5 (the hidden input
//       the legacy never updated). Enemy totals skip unconfigured monsters. Returns
//       { level, color } where level is the 6-tier label (TRIVIAL..TPK RISK) or "-"
//       and color is the legacy rgba / var(--text-subtle) string. NOTE: the legacy
//       computed an estimatedCR / totalCR (9458-9460) that it never used; it is
//       intentionally omitted here because it cannot affect the output.
//   - computeSystemStats(players, monsters, activeMonsterCount)
//       the system-state block (9419-9432): the four #enc-stat-* values. Reuses
//       computeEncounterSummary for the monster aggregates (no re-aggregation):
//       players = players.length, playersHp = sum of player hp, monsters =
//       totalMaxHP > 0 ? activeCount : 0, monstersHp = totalMaxHP > 0 ?
//       totalCurrentHP : 0. Raw numbers (the legacy showed 0, never "-").
//
// NOT here (owned by other runtimes / deferred): the multi-encounter store,
// encounter name, encounter selection / reset, and cloud persistence
// (viEncounterStore / viSwitchEncounter / saveToCloud) are Save / Cloud. None of
// that is in this module. No optional chaining. No em dash.

// Aggregate the encounter summary over the active, configured monsters. Pure:
// reads only the passed array and count, mutates nothing, returns a plain object.
export function computeEncounterSummary(monsters, activeMonsterCount) {
  let totalCurrentHP = 0;
  let totalMaxHP = 0;
  let activeCount = 0;
  let legendaryActionsRemaining = 0;

  let limit = activeMonsterCount;
  if (limit > monsters.length) { limit = monsters.length; }

  for (let i = 0; i < limit; i++) {
    const m = monsters[i];
    // Skip monsters that have not been configured yet (no HP and no selection).
    if (m.maxHP === 0 && !m.selectedMonster) { continue; }

    totalCurrentHP += m.currentHP || 0;
    totalMaxHP += m.maxHP || 0;
    if (m.currentHP > 0) { activeCount++; }

    const remaining = (m.maxLegendaryActions || 0) - (m.usedLegendaryActions || 0);
    if (remaining > 0) { legendaryActionsRemaining += remaining; }
  }

  return { totalCurrentHP, totalMaxHP, activeCount, legendaryActionsRemaining };
}

// Format the aggregates into the exact display strings the legacy wrote into
// totalHP / activeMonsters / legendaryActionsRemaining. When no max HP exists
// across the encounter, every field is the placeholder "-".
export function encounterSummaryDisplay(summary) {
  if (summary.totalMaxHP > 0) {
    return {
      totalHP: summary.totalCurrentHP + "/" + summary.totalMaxHP,
      activeMonsters: String(summary.activeCount),
      legendaryActionsRemaining: String(summary.legendaryActionsRemaining)
    };
  }
  return {
    totalHP: "-",
    activeMonsters: "-",
    legendaryActionsRemaining: "-"
  };
}

// ---- Stage 65 helpers ------------------------------------------------------

// Sum the hp of all players (viUpdateBalanceBar 8955 / system-state 8967 input).
function sumPlayerHp(players) {
  const list = Array.isArray(players) ? players : [];
  let total = 0;
  for (let i = 0; i < list.length; i++) {
    total += (list[i] && list[i].hp) || 0;
  }
  return total;
}

// Sum the maxHp of all players (threat partyHP input, 9422).
function sumPlayerMaxHp(players) {
  const list = Array.isArray(players) ? players : [];
  let total = 0;
  for (let i = 0; i < list.length; i++) {
    total += (list[i] && list[i].maxHp) || 0;
  }
  return total;
}

// Balance bar (viUpdateBalanceBar 8954-8969). monstersHP is the raw sum of
// currentHP over the first activeMonsterCount monsters (no configured-skip, exactly
// as the legacy summed). The percents are .toFixed(1) strings so the rendered width
// matches the legacy character-for-character (e.g. "0.0%").
export function computeBalanceBar(players, monsters, activeMonsterCount) {
  const list = Array.isArray(monsters) ? monsters : [];
  const playersHP = sumPlayerHp(players);

  let monstersHP = 0;
  let limit = activeMonsterCount;
  if (limit > list.length) { limit = list.length; }
  for (let i = 0; i < limit; i++) {
    monstersHP += (list[i] && list[i].currentHP) || 0;
  }

  const total = (playersHP + monstersHP) || 1;
  const playersPct = (playersHP / total * 100).toFixed(1);
  const monstersPct = (monstersHP / total * 100).toFixed(1);

  return { playersHP, monstersHP, playersPct, monstersPct };
}

// Threat assessment (updateThreatAssessment 9439-9508). partySize / partyHP are
// derived from players the way updateEncounterSummary fed the hidden inputs
// (9421-9422); partyLevel is the legacy constant 5. Returns the label + colour the
// legacy wrote into #threatLevel.
export function computeThreat(players, monsters, activeMonsterCount) {
  const partySize = (Array.isArray(players) ? players.length : 0) || 4;
  const partyLevel = 5;
  const partyHP = sumPlayerMaxHp(players) || 120;

  // The enemy totals the legacy threat loop produced (9450-9461) are identical to
  // the summary aggregation (same skip condition and fields), so reuse it rather
  // than re-looping. (The legacy estimatedCR / totalCR was never used; omitted.)
  const summary = computeEncounterSummary(monsters, activeMonsterCount);
  const totalEnemyHP = summary.totalCurrentHP;
  const totalEnemyMaxHP = summary.totalMaxHP;
  const activeEnemies = summary.activeCount;

  const subtle = { level: '-', color: 'var(--text-subtle)' };
  if (partySize === 0 || partyLevel === 0 || partyHP === 0) { return subtle; }
  if (totalEnemyMaxHP === 0 || activeEnemies === 0) { return subtle; }

  const hpRatio = totalEnemyHP / partyHP;
  const actionEconomyRatio = activeEnemies / partySize;

  let level = 'TRIVIAL';
  let color = 'rgba(134,239,172,0.9)';

  if (hpRatio < 0.3 && actionEconomyRatio <= 0.5) {
    level = 'TRIVIAL';
    color = 'rgba(134,239,172,0.9)';
  } else if (hpRatio < 0.6 && actionEconomyRatio <= 0.75) {
    level = 'EASY';
    color = 'rgba(134,239,172,0.8)';
  } else if (hpRatio < 1.0 && actionEconomyRatio <= 1.0) {
    level = 'MEDIUM';
    color = 'rgba(252,211,77,0.9)';
  } else if (hpRatio < 1.5 && actionEconomyRatio <= 1.5) {
    level = 'HARD';
    color = 'rgba(252,165,165,0.8)';
  } else if (hpRatio < 2.0 || actionEconomyRatio <= 2.0) {
    level = 'DEADLY';
    color = 'rgba(252,165,165,0.9)';
  } else {
    level = 'TPK RISK';
    color = 'rgba(252,165,165,1)';
  }

  return { level, color };
}

// System-state stats (the enc-stat-* block, 9419-9432). Reuses
// computeEncounterSummary for the monster aggregates rather than re-summing.
export function computeSystemStats(players, monsters, activeMonsterCount) {
  const summary = computeEncounterSummary(monsters, activeMonsterCount);
  const playerCount = Array.isArray(players) ? players.length : 0;
  const playersHpTotal = sumPlayerHp(players);

  return {
    players: playerCount,
    playersHp: playersHpTotal,
    monsters: summary.totalMaxHP > 0 ? summary.activeCount : 0,
    monstersHp: summary.totalMaxHP > 0 ? summary.totalCurrentHP : 0
  };
}
